import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "@/translations/TranslationContext";
import whatcando from "@/assets/whatcando.jpg";
import "./WhatAlgebraCanDo.css";

function WhatAlgebraCanDo() {
  const { t } = useTranslation();
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const imageVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.7, ease: "easeOut" }
    }
  };

  const listItemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: (i: number) => ({
      x: 0,
      opacity: 1,
      transition: {
        delay: 0.2 * i,
        duration: 0.4
      }
    })
  };

  const highlightStyle = {
    color: "#007bff",
    fontWeight: "bold"
  };

  return (
    <>
      <div className="whatcando">
        <div className="container">
          <div className="row" ref={ref}>
            <div className="col-md-6">
              <motion.img 
                src={whatcando} 
                alt="whatcando"
                initial="hidden"
                animate={controls}
                variants={imageVariants}
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <motion.div 
              className="col-md-6 whatcandotext"
              initial="hidden"
              animate={controls}
              variants={containerVariants}
            >
              <motion.h2 variants={itemVariants}>
                {t.whatAlgebraCan.title.part1}
                <span style={highlightStyle}>{t.whatAlgebraCan.title.highlight}</span>
                {t.whatAlgebraCan.title.part2}
              </motion.h2>
              <motion.p variants={itemVariants}>
                {t.whatAlgebraCan.description}
              </motion.p>
              <motion.ul variants={containerVariants}>
                <motion.li 
                  custom={0}
                  variants={listItemVariants}
                  whileHover={{ x: 5, transition: { duration: 0.2 } }}
                >
                  <motion.span 
                    className="wcdcont"
                    whileHover={{ 
                      scale: 1.1, 
                      backgroundColor: "#007bff",
                      transition: { duration: 0.2 } 
                    }}
                  >
                    1
                  </motion.span>
                  <span>{t.whatAlgebraCan.points.experience}</span>
                </motion.li>
                <motion.li 
                  custom={1}
                  variants={listItemVariants}
                  whileHover={{ x: 5, transition: { duration: 0.2 } }}
                >
                  <motion.span 
                    className="wcdcont"
                    whileHover={{ 
                      scale: 1.1, 
                      backgroundColor: "#007bff",
                      transition: { duration: 0.2 } 
                    }}
                  >
                    2
                  </motion.span>
                  <span>{t.whatAlgebraCan.points.dedicated}</span>
                </motion.li>
                <motion.li 
                  custom={2}
                  variants={listItemVariants}
                  whileHover={{ x: 5, transition: { duration: 0.2 } }}
                >
                  <motion.span 
                    className="wcdcont"
                    whileHover={{ 
                      scale: 1.1, 
                      backgroundColor: "#007bff",
                      transition: { duration: 0.2 } 
                    }}
                  >
                    3
                  </motion.span>
                  <span>{t.whatAlgebraCan.points.advanced}</span>
                </motion.li>
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WhatAlgebraCanDo;
